/**
 * Die Felder der Registrierung prüfen — **am Feld, bevor abgeschickt wird**.
 *
 * Bis dahin gab es zwei Arten, an einer Regel zu scheitern, und keine sagte, an
 * welcher: Die Blase des Browsers (`minLength`, `required`) verschwindet beim
 * nächsten Klick und erscheint auf manchem Telefon gar nicht; und was der
 * Browser durchlässt, der Vertrag aber nicht — `a@b` ist für `type="email"`
 * eine Adresse, für `EmailSchema` nicht —, kam als „Die Anfrage entspricht nicht
 * dem Vertrag." zurück. Dazu ein gesperrter Knopf ohne Begründung.
 *
 * **Die Regeln sind abgeschrieben und werden gegen die Quelle gehalten.** Diese
 * App darf `@hausmeter/contracts` nicht ins Bündel ziehen; `registration-fields.test.ts`
 * prüft jede Regel gegen `RegisterRequestSchema`, wie `legal.test.ts` die Fristen
 * gegen ihre Quelle. Wer den Vertrag ändert, bekommt einen roten Test statt einer
 * Oberfläche, die etwas anderes verlangt als der Server.
 *
 * **Und was trotzdem vom Server abgelehnt wird, landet ebenfalls am Feld**
 * (`fieldErrorsFromDetails`): Die Antwort nennt je Verstoß den Pfad, und die
 * Meldung dazu ist ein englischer Satz aus Zod — deshalb steht hier je Feld ein
 * eigener.
 */

/** Die Felder, die eine Registrierung prüft — die Schlüssel sind die des Vertrags. */
export type RegistrationField = "firstName" | "lastName" | "email" | "password" | "terms";

export type FieldErrors = Partial<Record<RegistrationField, string>>;

/** `PasswordSchema` — Mindestlänge statt Zeichenklassen. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
/** `PersonNameSchema`. */
export const PERSON_NAME_MAX_LENGTH = 80;
/** `EmailSchema`. */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Das Muster hinter `z.email()` — **nicht** das des Browsers. Der lässt `a@b`
 * und Umlaute vor dem `@` zu; der Vertrag verlangt eine Endung aus mindestens
 * zwei Buchstaben und ASCII. Geprüft wird gegen den Vertrag, sonst stünde am
 * Feld kein Fehler und nach dem Absenden trotzdem einer.
 */
const EMAIL_PATTERN =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;

export interface RegistrationInput {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly password: string;
  readonly termsAccepted: boolean;
}

/**
 * Was der Mensch vor dem Formular noch tun muss, je Feld ein Satz.
 *
 * Ein leeres Ergebnis heißt „abschicken". Ein **nur aus Leerzeichen**
 * bestehender Name gilt als leer, obwohl `PersonNameSchema` ihn annähme: Auf
 * einem Namensschild stünde nichts, und der Account hieße danach so.
 */
export function registrationErrors(input: RegistrationInput): FieldErrors {
  const errors: FieldErrors = {};

  const firstName = nameError(input.firstName, "Vornamen");
  if (firstName !== undefined) errors.firstName = firstName;
  const lastName = nameError(input.lastName, "Nachnamen");
  if (lastName !== undefined) errors.lastName = lastName;

  const email = emailError(input.email);
  if (email !== undefined) errors.email = email;

  const password = passwordError(input.password);
  if (password !== undefined) errors.password = password;

  if (!input.termsAccepted) {
    errors.terms = "Ohne deine Zustimmung kann kein Konto entstehen.";
  }

  return errors;
}

export function nameError(value: string, what: "Vornamen" | "Nachnamen"): string | undefined {
  if (value.trim() === "") return `Bitte gib deinen ${what} an.`;
  if (value.length > PERSON_NAME_MAX_LENGTH) return `Höchstens ${PERSON_NAME_MAX_LENGTH} Zeichen.`;
  return undefined;
}

export function emailError(value: string): string | undefined {
  if (value.trim() === "") return "Bitte gib deine E-Mail-Adresse an.";
  if (value.length > EMAIL_MAX_LENGTH) return `Höchstens ${EMAIL_MAX_LENGTH} Zeichen.`;
  if (!EMAIL_PATTERN.test(value)) {
    return /\s/.test(value)
      ? "Die Adresse enthält ein Leerzeichen."
      : "Das ist keine vollständige E-Mail-Adresse — etwa name@beispiel.de. Umlaute vor dem @ sind nicht möglich.";
  }
  return undefined;
}

/**
 * **Sagt, wie viel fehlt**, und nicht nur, dass etwas fehlt: „Noch 3 Zeichen"
 * beantwortet die Frage, die sich beim Nachtippen stellt.
 */
export function passwordError(value: string): string | undefined {
  if (value.length === 0) {
    return `Bitte wähle ein Passwort mit mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`;
  }
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Noch ${PASSWORD_MIN_LENGTH - value.length} Zeichen — mindestens ${PASSWORD_MIN_LENGTH}.`;
  }
  if (value.length > PASSWORD_MAX_LENGTH) return `Höchstens ${PASSWORD_MAX_LENGTH} Zeichen.`;
  return undefined;
}

/** Ein Eintrag aus `ErrorResponse.error.details`. */
export interface ErrorDetail {
  readonly path?: readonly string[];
  readonly code: string;
  readonly message: string;
}

const FIELD_OF_PATH: Readonly<Record<string, Exclude<RegistrationField, "terms">>> = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  password: "password",
  newPassword: "password",
};

/**
 * Die Verstöße aus einer Antwort des Servers, übersetzt auf die Felder.
 *
 * Die Meldung des Servers wird **nicht** übernommen — sie ist Zods Englisch.
 * Der Satz kommt aus derselben Prüfung wie vor dem Absenden, mit dem Wert, der
 * abgeschickt wurde; stimmt die Oberfläche mit dem Vertrag überein, liefert sie
 * dort ohnehin einen. Tut sie es nicht, steht am Feld der allgemeine Satz statt
 * gar keiner.
 */
export function fieldErrorsFromDetails(
  details: readonly ErrorDetail[] | undefined,
  sent: Partial<Omit<RegistrationInput, "termsAccepted">>,
): FieldErrors {
  const errors: FieldErrors = {};
  for (const detail of details ?? []) {
    const field = FIELD_OF_PATH[detail.path?.[0] ?? ""];
    if (field === undefined || errors[field] !== undefined) continue;
    errors[field] =
      (field === "email"
        ? emailError(sent.email ?? "")
        : field === "password"
          ? passwordError(sent.password ?? "")
          : nameError(sent[field] ?? "", field === "firstName" ? "Vornamen" : "Nachnamen")) ??
      "Diese Angabe nimmt der Server nicht an.";
  }
  return errors;
}
